import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { environment } from 'src/environments/environment';
import { Batch, BatchAutoCreateData, BatchCreatePayload, BatchItem, LinkOrderItemToBatchPayload, PrintBarcodePayload, PrintLabelPayload, ScanBatchItemPayload } from 'src/interfaces/batch';
import { Order, RawOrderItem } from 'src/interfaces/order';
import { FulfilmentPayload, OrderItem } from 'src/interfaces/order-item';
import { BatchItemListDialogComponent } from '../components/batch-item-list-dialog/batch-item-list-dialog.component';
import { BatchPrintDialogComponent } from '../components/batch-print-dialog/batch-print-dialog.component';
import { LinkOrderItemToBatchDialogComponent } from '../components/link-order-item-to-batch-dialog/link-order-item-to-batch-dialog.component';
import { OrderFulfilmentDialogComponent } from '../components/order-fulfilment-dialog/order-fulfilment-dialog.component';
import { OrderItemDetailsDialogComponent } from '../components/order-item-details-dialog/order-item-details-dialog.component';
import { PrintLabelsDialogComponent } from '../components/print-labels-dialog/print-labels-dialog.component';
import { AlertService } from './alert.service';
import { AuthService } from './auth.service';
import { ConfirmationService } from './confirmation.service';
import { LocalStorageService } from './local-storage.service';
import { UtilsService } from './utils.service';

@Injectable({
  providedIn: 'root'
})
export class BatchService {

  baseUrl: string = environment.api.baseUrl;
  printServerBaseUrl: string = environment.api.printServerBaseUrl;
  headers: HttpHeaders;
  totalNumberOfOrders: number = 0;
  totalNumberOfBatches: number = 0;
  totalNumberOfBatchItems: number = 0;

  constructor(
    private _auth: AuthService,
    private _alertService: AlertService,
    private _http: HttpClient,
    private _dialog: MatDialog,
    private _localStorageService: LocalStorageService,
    private _utilsService: UtilsService,
    private _confirmationService: ConfirmationService
  ) {
    this
      ._auth
      .generateRequestHeaders()
      .then((headers) => {
        this.headers = headers;
      })
  }

  async createBatch(payload: BatchCreatePayload): Promise<Batch | null> {

    const confirm = await this._confirmationService.confirmSave();

    if (!confirm) return null;

    try {

      const { message, data } = await this
        ._http
        .post<any>(`${this.baseUrl}/batch`, payload, {
          headers: this.headers
        }).toPromise();
      this._alertService.openSnackBar(message);
      return data;

    } catch (error) {

      this._utilsService
        .showError(error);

      return null;
    }
  }

  async requestBatch(payload: BatchAutoCreateData) {

    const { confirm, batch_number } = await this._confirmationService
      .confirmBatchRequest("Request new batch", "Would you like to request a batch");

    if (!confirm) return false;

    payload.batch_number = batch_number;

    try {
      const { message } = await this
        ._http
        .post<any>(`${this.baseUrl}/batch/by-package`, payload, {
          headers: this.headers
        })
        .toPromise();
      this._alertService.openSnackBar(message);
      return true;
    } catch (error) {

      this._utilsService
        .showError(error);

      return false;
    }
  }

  async linkToBatch(batchId: number, payload: LinkOrderItemToBatchPayload) {

    try {
      const { message, success } = await this
        ._http
        .post<any>(
          `${this.baseUrl}/batch/${batchId}/link-order-item`,
          payload,
          {
            headers: this.headers
          })
        .toPromise();
      this._alertService.openSnackBar(message);
      return true;
    } catch (error) {

      this._utilsService
        .showError(error);

      return false;
    }
  }

  async editBatch(payload: BatchCreatePayload, batchId: number) {

    const confirm = await this._confirmationService.confirmSave();

    if (!confirm) return false;

    try {

      const { success, message } = await this
        ._http
        .put<any>(`${this.baseUrl}/batch/${batchId}`, payload, {
          headers: this.headers
        })
        .toPromise();
      this._alertService.openSnackBar(message);
      return success;

    } catch (error) {
      this._utilsService
        .showError(error);
      return false;
    }
  }

  async getBatches(
    page: number,
    limit: number,
    search: string = "",
    medicine_id: number | null = null,
    batch_status_id: number | null = null
  ): Promise<Batch[]> {
    try {

      let queryString: string = `?page=${page}&limit=${limit}`;
      if (search) queryString += `&search=${search}`;
      if (medicine_id) queryString += `&medicine_id=${medicine_id}`;
      if (batch_status_id) queryString += `&batch_status_id=${batch_status_id}`;

      const response = await this
        ._http
        .get<any>(`${this.baseUrl}/batch${queryString}`)
        .toPromise();
      this.totalNumberOfBatches = response.data.total;
      return response.data.results;
    } catch (error) {

      this._utilsService
        .showError(error);

      return [];
    }
  }



  async reportBatch(
    payload: { start_date: string, end_date: string }
  ): Promise<Batch[]> {
    try {

      const response = await this
        ._http
        .post<any>(`${this.baseUrl}/report/batch`, payload)
        .toPromise();
    
      return response.data;

    } catch (error) {

      this._utilsService
        .showError(error);

      return [];
    }
  }

  async searchBatches(page: number, limit: number, search: string): Promise<Batch[]> {
    try {
      const response = await this
        ._http
        .get<any>(`${this.baseUrl}/batch?page=${page}&limit=${limit}&search=${search}`)
        .toPromise();
      return response.data.results;
    } catch (error) {

      this._utilsService
        .showError(error);

      return [];
    }
  }

  getTotalNumberOfBatches() {
    return this.totalNumberOfBatches;
  }

  async getBatch(id: number): Promise<Batch | null> {
    try {
      const response = await this
        ._http
        .get<any>(`${this.baseUrl}/batch/${id}`)
        .toPromise();

      return response.data;
    } catch (error) {
      this._utilsService
        .showError(error);
      return null;
    }
  }

  async getBatchByBatchNumber(batchNumber: string): Promise<Batch | null> {
    try {
      const response = await this
        ._http
        .get<any>(`${this.baseUrl}/batch/number/${batchNumber}`)
        .toPromise();

      return response.data;
    } catch (error) {
      // this._utilsService
      //   .showError(error);
      return null;
    }
  }

  openLinkToBatchDialog(title: string, message: string, orderItem: OrderItem): Promise<boolean> {

    const dialogRef = this._dialog.open(LinkOrderItemToBatchDialogComponent, {
      width: '420px',
      panelClass: 'mbp-confirm-dialog',
      data: {
        title,
        message,
        orderItem
      }
    });

    return dialogRef.afterClosed().toPromise();
  }

  openOrderFulfilmentDialog(
    order: Order,
    orderItems: OrderItem[],
    title: string = "Order fulfilment",
    message: string = "Order fulfilment"): Promise<boolean> {

    const dialogRef = this._dialog.open(OrderFulfilmentDialogComponent, {
      width: '1000px',
      data: {
        title,
        message,
        order,
        orderItems
      }
    });

    return dialogRef.afterClosed().toPromise();
  }


  async openBatchItemListDialog(
    orderItem: OrderItem,
    title: string = "Batch items",
    message: string = "Batch items for fulfilment"): Promise<boolean> {

    if (!orderItem.id) return false;

    const limitStr = this._localStorageService.get('BATCHITEMS', 'LIMIT');

    const limit: number = limitStr ? parseInt(limitStr) : 5;

    const batchItems: BatchItem[] = await this.getBatchItems(orderItem.id, 1, limit, '');

    if (batchItems.length === 0) {
      this._alertService.warningAlert("To scan an order item you need to first link to a batch.")
      return false;
    }

    const dialogRef = this._dialog.open(BatchItemListDialogComponent, {
      width: '900px',
      maxWidth: '95vw',
      panelClass: 'mbp-confirm-dialog',
      data: {
        title,
        message,
        batchItems,
        orderItem
      }
    });

    return dialogRef.afterClosed().toPromise();
  }

  async getBatchItems(orderItemId: number, page: number, limit: number, search: string = ""): Promise<BatchItem[]> {
    try {

      const { data } = await this
        ._http
        .get<any>(`${this.baseUrl}/fulfilment/${orderItemId}?page=${page}&limit=${limit}"`)
        .toPromise();

      this.totalNumberOfBatchItems = data.total;
      return data;

    } catch (error) {
      this._utilsService
        .showError(error);
      return [];
    }
  }

  getTotalNumberOfBatchItems(): number {
    return this.totalNumberOfBatchItems;
  }

  async scanBatchItem(
    orderItemId: number,
    batchItemId: number,
    payload: ScanBatchItemPayload
  ): Promise<boolean> {
    try {

      const { success } = await this._http
        .post<any>(`${this.baseUrl}/fulfilment/${orderItemId}/scan/${batchItemId}`,
          payload
          , {
            headers: this.headers
          })
        .toPromise();

      return success;
    } catch (error) {
      this._utilsService
        .showError(error);
      return false;
    }
  }

  async scanOrderItem(orderItemId: number, payload: FulfilmentPayload): Promise<boolean> {
    try {
      const { success, message } = await this._http
        .post<any>(`${this.baseUrl}/fulfilment/${orderItemId}/scan`, payload)
        .toPromise();
      this._alertService.infoAlert(message);
      return success;
    } catch (error) {
      this._utilsService.showError(error);
      return false;
    }
  }

  async fulfillOrderItem(orderItemId: number): Promise<boolean> {
    try {
      const { success, message } = await this._http
        .get<any>(`${this.baseUrl}/fulfilment/${orderItemId}/fulfill`)
        .toPromise();
      this._alertService.infoAlert(message);
      return success;
    } catch (error) {
      this._utilsService.showError(error);
      return false;
    }
  }

  openBatchPrintDialog(title: string, message: string, printType: string, batch: Batch): Promise<boolean> {

    const dialogRef = this._dialog.open(BatchPrintDialogComponent, {
      width: '420px',
      panelClass: 'mbp-confirm-dialog',
      data: {
        title,
        message,
        printType,
        batch
      }
    });

    return dialogRef
      .afterClosed()
      .toPromise();
  }

  openPrintLabelsDialog(title: string, message: string, orderItems: OrderItem[], order: Order): Promise<boolean> {

    const dialogRef = this._dialog.open(PrintLabelsDialogComponent, {
      width: '350px',
      data: {
        title,
        message,
        orderItems,
        order
      }
    });

    return dialogRef
      .afterClosed()
      .toPromise();
  }

  async printLabel(payload: PrintLabelPayload): Promise<boolean> {

    try {

      const { message } = await this
        ._http
        .post<any>(`${this.printServerBaseUrl}/label/`, payload)
        .toPromise();

      this._alertService.infoAlert(message);

      return true;

    } catch (error) {
      this._utilsService.showError(error);
      return false;
    }
  }

  async printBarcode(payload: PrintBarcodePayload): Promise<boolean> {

    try {

      const { message } = await this
        ._http
        .post<any>(
          `${this.printServerBaseUrl}/barcode/`, payload)
        .toPromise();

      this._alertService.infoAlert(message);

      return true;

    } catch (error) {
      this._utilsService.showError(error);
      return false;
    }
  }

  openOrderItemDetailsDialog(orderItem: OrderItem | null = null, rawOrderItem: RawOrderItem | null = null): Promise<boolean> {

    const dialogRef = this._dialog.open(OrderItemDetailsDialogComponent, {
      width: '560px',
      maxWidth: '95vw',
      panelClass: 'mbp-confirm-dialog',
      data: {
        orderItem: orderItem,
        rawOrderItem: rawOrderItem
      }
    });

    return dialogRef
      .afterClosed()
      .toPromise();
  }
}


